import { ExternalLink, type LucideIcon } from "lucide-react";
import { Link, useLocation } from "react-router-dom";

import {
  SidebarGroup,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
} from "@/components/ui/sidebar";

export interface NavSecondaryItem {
  name: string;
  url?: string;
  activePrefix?: string;
  icon: LucideIcon;
  target?: string;
  onClick?: () => void;
  badgeCount?: number;
}

export function NavSecondary({
  items,
}: {
  items: NavSecondaryItem[];
}) {
  const location = useLocation();
  const pathname = location.pathname;

  return (
    <SidebarGroup className="px-0">
      <SidebarMenu>
        {items.map((item) => {
          if (item.onClick) {
            const hasBadge = !!item.badgeCount && item.badgeCount > 0;
            return (
              <SidebarMenuItem key={item.name}>
                <SidebarMenuButton
                  tooltip={item.name}
                  onClick={item.onClick}
                  className="cursor-pointer"
                >
                  <item.icon />
                  <span>{item.name}</span>
                </SidebarMenuButton>
                {hasBadge && (
                  <>
                    {/* Expanded: numeric badge, capped at 9+ */}
                    <SidebarMenuBadge className="bg-sidebar-primary text-sidebar-primary-foreground">
                      {item.badgeCount! > 9 ? "9+" : item.badgeCount}
                    </SidebarMenuBadge>
                    {/* Collapsed (icon mode): presence dot, since the numeric
                        badge is hidden when collapsed */}
                    <span className="bg-sidebar-primary pointer-events-none absolute top-1 right-1 hidden size-2 rounded-full group-data-[collapsible=icon]:block" />
                  </>
                )}
              </SidebarMenuItem>
            );
          }

          const isActive =
            !!item.url &&
            (pathname === (item.activePrefix ?? item.url) ||
              pathname.startsWith(`${item.activePrefix ?? item.url}/`));

          if (item.target === "_blank") {
            return (
              <SidebarMenuItem key={item.name}>
                <SidebarMenuButton asChild tooltip={item.name} isActive={isActive}>
                  <a
                    href={item.url}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <item.icon />
                    <span>{item.name}</span>
                    {/* Affordance that this link leaves the app / opens a new
                        tab. Hidden when collapsed (no room beside the icon). */}
                    <ExternalLink className="ml-auto size-3! opacity-50 group-hover/menu-item:opacity-100 group-data-[collapsible=icon]:hidden" />
                  </a>
                </SidebarMenuButton>
              </SidebarMenuItem>
            );
          }

          return (
            <SidebarMenuItem key={item.name}>
              <SidebarMenuButton asChild tooltip={item.name} isActive={isActive}>
                <Link to={item.url ?? ""}>
                  <item.icon />
                  <span>{item.name}</span>
                </Link>
              </SidebarMenuButton>
            </SidebarMenuItem>
          );
        })}
      </SidebarMenu>
    </SidebarGroup>
  );
}
